import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Title } from 'react-admin';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { withStyles } from '@material-ui/core/styles';
import {
  confirmTotp,
  fetchMyTotpStatus,
  regenerateBackupCodes,
  setupTotp,
} from '../api';

const styles = theme => ({
  root: {
    padding: '1em',
    maxWidth: 640,
    margin: '0 auto',
  },
  card: {
    marginBottom: '1em',
  },
  statusBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75em',
    padding: '0.75em 1em',
    marginBottom: '1em',
    backgroundColor: '#e8f5e9',
    color: '#1b5e20',
    border: '1px solid #c8e6c9',
    borderRadius: 4,
  },
  statusIcon: {
    color: '#2e7d32',
  },
  qr: {
    display: 'block',
    margin: '1em auto',
    width: 220,
    height: 220,
  },
  secret: {
    fontFamily: 'monospace',
    background: theme.palette.grey[100],
    padding: '0.5em',
    wordBreak: 'break-all',
  },
  codes: {
    fontFamily: 'monospace',
    background: theme.palette.grey[100],
    padding: '0.75em',
    columns: 2,
    listStyle: 'none',
    margin: '0.5em 0',
  },
  warning: {
    color: theme.palette.error.main,
    margin: '0.5em 0',
  },
  error: {
    color: theme.palette.error.main,
    margin: '0.5em 0',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5em',
    marginTop: '0.5em',
  },
  confirmStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1em',
    marginTop: '2em',
  },
  confirmInput: {
    width: 160,
    '& input': {
      textAlign: 'center',
      letterSpacing: '0.3em',
      fontSize: '1.1em',
    },
  },
  input: {
    marginRight: '0.5em',
  },
});

class TwoFactorSettings extends Component {
  state = {
    loading: true,
    enabled: false,
    setupData: null, // { secret, otpauthUrl, qrCodeDataUrl }
    confirmCode: '',
    backupCodes: null,
    regenCode: '',
    busy: false,
    error: null,
  };

  async componentDidMount() {
    await this.refreshStatus();
  }

  refreshStatus = async () => {
    this.setState({ loading: true, error: null });
    try {
      const { data } = await fetchMyTotpStatus();
      this.setState({ loading: false, enabled: data.totpEnabled });
    } catch (err) {
      this.setState({ loading: false, error: extractMessage(err) });
    }
  };

  handleStartSetup = async () => {
    this.setState({ busy: true, error: null });
    try {
      const { data } = await setupTotp();
      this.setState({ busy: false, setupData: data, backupCodes: null });
    } catch (err) {
      this.setState({ busy: false, error: extractMessage(err) });
    }
  };

  handleConfirm = async () => {
    const { confirmCode } = this.state;
    this.setState({ busy: true, error: null });
    try {
      const { data } = await confirmTotp({ code: confirmCode });
      this.setState({
        busy: false,
        backupCodes: data.backupCodes,
        setupData: null,
        confirmCode: '',
        enabled: true,
      });
    } catch (err) {
      this.setState({ busy: false, error: extractMessage(err) });
    }
  };

  handleRegen = async () => {
    const { regenCode } = this.state;
    this.setState({ busy: true, error: null });
    try {
      const { data } = await regenerateBackupCodes({ code: regenCode });
      this.setState({
        busy: false,
        backupCodes: data.backupCodes,
        regenCode: '',
      });
    } catch (err) {
      this.setState({ busy: false, error: extractMessage(err) });
    }
  };

  handleField = field => event => this.setState({ [field]: event.target.value });

  onEnter = (handler, canSubmit) => event => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (!this.state.busy && canSubmit) handler();
  };

  render() {
    const { classes } = this.props;
    const {
      loading,
      enabled,
      setupData,
      backupCodes,
      busy,
      error,
      confirmCode,
      regenCode,
    } = this.state;

    if (loading) {
      return (
        <div className={classes.root}>
          <Title title="Two-Factor Authentication" />
          <CircularProgress />
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Title title="Two-Factor Authentication" />

        {!enabled && !setupData && (
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6">Two-factor authentication is off</Typography>
              <Typography variant="body2" style={{ margin: '0.5em 0' }}>
                Enable 2FA to protect your account. You will need an authenticator app such as
                Google Authenticator, Authy, or 1Password.
              </Typography>
              <Button variant="contained" color="primary" onClick={this.handleStartSetup} disabled={busy}>
                {busy ? <CircularProgress size={20} /> : 'Enable 2FA'}
              </Button>
            </CardContent>
          </Card>
        )}

        {setupData && (
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6">Scan this QR code</Typography>
              <Typography variant="body2">
                Open your authenticator app and scan this QR code, then enter the 6-digit code it
                generates to confirm.
              </Typography>
              <img className={classes.qr} src={setupData.qrCodeDataUrl} alt="2FA QR code" />
              <Typography variant="body2">If you cannot scan, enter this secret manually:</Typography>
              <div className={classes.secret}>{setupData.secret}</div>
              <div className={classes.confirmStep}>
                <TextField
                  label="6-digit code"
                  value={confirmCode}
                  onChange={this.handleField('confirmCode')}
                  onKeyDown={this.onEnter(this.handleConfirm, Boolean(confirmCode))}
                  className={classes.confirmInput}
                  disabled={busy}
                  autoFocus
                  inputProps={{ autoComplete: 'one-time-code', maxLength: 6 }}
                />
                <Button variant="contained" color="primary" onClick={this.handleConfirm} disabled={busy || !confirmCode}>
                  {busy ? <CircularProgress size={20} /> : 'Confirm'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {backupCodes && (
          <Card className={classes.card}>
            <CardContent>
              <Typography variant="h6">Save your backup codes</Typography>
              <Typography variant="body2" className={classes.warning}>
                Save these codes somewhere safe NOW. They will not be shown again. Each code can be
                used once if you lose access to your authenticator.
              </Typography>
              <ul className={classes.codes}>
                {backupCodes.map(c => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
              <Button
                variant="contained"
                color="primary"
                onClick={() => this.setState({ backupCodes: null })}
              >
                I have saved them
              </Button>
            </CardContent>
          </Card>
        )}

        {enabled && !setupData && !backupCodes && (
          <React.Fragment>
            <div className={classes.statusBanner}>
              <CheckCircleIcon className={classes.statusIcon} />
              <Typography variant="body2" style={{ color: 'inherit' }}>
                <strong>Two-factor authentication is on.</strong> You will be asked for a code each
                time you sign in.
              </Typography>
            </div>

            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6">Regenerate backup codes</Typography>
                <Typography variant="body2">
                  Enter your current code (or an unused backup code) to generate a new set. Old
                  backup codes will stop working.
                </Typography>
                <div className={classes.row}>
                  <TextField
                    label="Code"
                    value={regenCode}
                    onChange={this.handleField('regenCode')}
                    onKeyDown={this.onEnter(this.handleRegen, Boolean(regenCode))}
                    className={classes.input}
                    disabled={busy}
                  />
                  <Button onClick={this.handleRegen} disabled={busy || !regenCode}>
                    {busy ? <CircularProgress size={20} /> : 'Regenerate'}
                  </Button>
                </div>
              </CardContent>
            </Card>

          </React.Fragment>
        )}

        {error && <div className={classes.error}>{error}</div>}
      </div>
    );
  }
}

TwoFactorSettings.propTypes = {
  classes: PropTypes.object.isRequired,
};

function extractMessage(err) {
  const message = (err && err.message) || 'Unknown error';
  const prefix = 'GraphQL Error: ';
  if (message.startsWith(prefix)) {
    try {
      const errors = JSON.parse(message.slice(prefix.length));
      if (Array.isArray(errors) && errors[0] && errors[0].message) {
        return errors[0].message;
      }
    } catch (e) {
      // fall through
    }
  }
  return message;
}

export default withStyles(styles)(TwoFactorSettings);
